/* PLAT-02: offline cache for chat history reads, plus a small outbox.
   How it works: `installOfflineCache()` wraps the API client's fetcher (`setFetcher` in lib/api.ts).
   Successful GETs of `/api/v1/chats...` (the chat list, a chat, its messages) are written to IndexedDB;
   when the network fails (or the dev proxy answers 502/503/504) the last copy is served instead, marked
   with the header `x-cx-offline: 1`. Nothing else is touched. Without IndexedDB (private windows,
   jsdom) an in-memory store keeps working for the session.
   A root-scoped service worker (offline app shell) needs a file at the site root, which is outside this
   folder; it is requested in docs/core-requests.md. */
import { getFetcher, setFetcher, type Fetcher } from '../../lib/api';

export interface CachedResponse { key: string; body: string; status: number; contentType: string; savedAt: number; size: number }
export interface OutboxItem { id?: number; method: string; url: string; body?: unknown; createdAt: number; label?: string }

export interface OfflineStore {
  readonly persistent: boolean;
  get(key: string): Promise<CachedResponse | undefined>;
  put(entry: CachedResponse): Promise<void>;
  all(): Promise<CachedResponse[]>;
  remove(key: string): Promise<void>;
  clear(): Promise<void>;
  outboxAdd(item: OutboxItem): Promise<number>;
  outboxAll(): Promise<OutboxItem[]>;
  outboxRemove(id: number): Promise<void>;
}

export const MAX_ENTRIES = 400;
export const ENABLED_KEY = 'cx.offline.enabled';

/** Chat history reads only. */
export function cacheKey(url: string): string | null {
  let u: URL;
  try { u = new URL(url, 'http://local.invalid'); } catch { return null; }
  if (!/^\/api\/v1\/chats(\/[^/]+(\/messages)?)?\/?$/.test(u.pathname)) return null;
  const params = [...u.searchParams.entries()].sort(([a], [b]) => a.localeCompare(b));
  const qs = new URLSearchParams(params).toString();
  return u.pathname + (qs ? `?${qs}` : '');
}

export function offlineEnabled(): boolean {
  try { return localStorage.getItem(ENABLED_KEY) !== '0'; } catch { return true; }
}
export function setOfflineEnabled(on: boolean): void {
  try { localStorage.setItem(ENABLED_KEY, on ? '1' : '0'); } catch { /* storage blocked */ }
}

const urlOf = (input: RequestInfo | URL): string => (typeof input === 'string' ? input : input instanceof URL ? input.href : input.url);
const methodOf = (input: RequestInfo | URL, init?: RequestInit): string =>
  (init?.method ?? (typeof input === 'object' && 'method' in input ? input.method : 'GET')).toUpperCase();

function toResponse(c: CachedResponse): Response {
  return new Response(c.body, { status: c.status, headers: { 'content-type': c.contentType, 'x-cx-offline': '1', 'x-cx-cached-at': new Date(c.savedAt).toISOString() } });
}

/** Wraps a fetcher with the read-through / fall-back cache. */
export function createOfflineFetcher(base: Fetcher, store: OfflineStore, opts: { enabled?: () => boolean; now?: () => number } = {}): Fetcher {
  const enabled = opts.enabled ?? offlineEnabled;
  const now = opts.now ?? Date.now;
  return async (input, init) => {
    const key = methodOf(input, init) === 'GET' ? cacheKey(urlOf(input)) : null;
    if (!key || !enabled()) return base(input, init);
    let res: Response;
    try {
      res = await base(input, init);
    } catch (err) {
      if ((err as Error)?.name === 'AbortError') throw err;
      const hit = await store.get(key).catch(() => undefined);
      if (hit) return toResponse(hit);
      throw err;
    }
    if (res.ok && typeof res.clone === 'function') {
      try {
        const body = await res.clone().text();
        await store.put({ key, body, status: res.status, contentType: res.headers?.get?.('content-type') ?? 'application/json', savedAt: now(), size: body.length });
        await prune(store);
      } catch { /* caching is best effort */ }
    } else if (res.status === 502 || res.status === 503 || res.status === 504) {
      const hit = await store.get(key).catch(() => undefined);
      if (hit) return toResponse(hit);
    }
    return res;
  };
}

async function prune(store: OfflineStore): Promise<void> {
  const all = await store.all();
  if (all.length <= MAX_ENTRIES) return;
  const old = all.sort((a, b) => a.savedAt - b.savedAt).slice(0, all.length - MAX_ENTRIES);
  for (const e of old) await store.remove(e.key);
}

/* ---------- stats, clear, outbox ---------- */

export interface CacheStats { entries: number; chats: number; bytes: number; lastSavedAt: number | null; pending: number; persistent: boolean; usage?: number; quota?: number }

export async function cacheStats(store: OfflineStore = getOfflineStore()): Promise<CacheStats> {
  const [all, outbox] = await Promise.all([store.all(), store.outboxAll()]);
  const chats = all.filter((e) => /^\/api\/v1\/chats\/[^/?]+$/.test(e.key.split('?')[0]!)).length;
  const stats: CacheStats = {
    entries: all.length,
    chats,
    bytes: all.reduce((n, e) => n + e.size, 0),
    lastSavedAt: all.reduce<number | null>((m, e) => (m === null || e.savedAt > m ? e.savedAt : m), null),
    pending: outbox.length,
    persistent: store.persistent,
  };
  try {
    const est = await navigator.storage?.estimate?.();
    if (est) { stats.usage = est.usage; stats.quota = est.quota; }
  } catch { /* not available */ }
  return stats;
}

/** Clears the chat cache and any Cache Storage entries this app created. The outbox is kept. */
export async function clearOfflineCache(store: OfflineStore = getOfflineStore()): Promise<void> {
  await store.clear();
  try {
    if (typeof caches !== 'undefined') for (const k of await caches.keys()) if (k.startsWith('cx-')) await caches.delete(k);
  } catch { /* Cache Storage unavailable */ }
}

/** Other features: queue a write made while offline. It is sent, in order, on `flushOutbox()` or when the browser comes back online. */
export function enqueueSync(item: Omit<OutboxItem, 'createdAt' | 'id'>, store: OfflineStore = getOfflineStore()): Promise<number> {
  return store.outboxAdd({ ...item, createdAt: Date.now() });
}

export async function flushOutbox(store: OfflineStore = getOfflineStore(), f: Fetcher = getFetcher()): Promise<{ sent: number; failed: number }> {
  const items = (await store.outboxAll()).sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
  let sent = 0;
  let failed = 0;
  for (const it of items) {
    try {
      const res = await f(it.url, { method: it.method, credentials: 'include', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: it.body === undefined ? undefined : JSON.stringify(it.body) });
      if (res.ok || (res.status >= 400 && res.status < 500)) { await store.outboxRemove(it.id!); sent++; } else { failed++; break; }
    } catch {
      failed++;
      break; // still offline: keep the order
    }
  }
  return { sent, failed };
}

/* ---------- stores ---------- */

export function createMemoryStore(): OfflineStore {
  const map = new Map<string, CachedResponse>();
  const out = new Map<number, OutboxItem>();
  let n = 0;
  return {
    persistent: false,
    get: async (k) => map.get(k),
    put: async (e) => { map.set(e.key, e); },
    all: async () => [...map.values()],
    remove: async (k) => { map.delete(k); },
    clear: async () => { map.clear(); },
    outboxAdd: async (item) => { const id = ++n; out.set(id, { ...item, id }); return id; },
    outboxAll: async () => [...out.values()],
    outboxRemove: async (id) => { out.delete(id); },
  };
}

const DB_NAME = 'cx-offline';
function req<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => { r.onsuccess = () => resolve(r.result); r.onerror = () => reject(r.error); });
}

export function createIdbStore(idb: IDBFactory): OfflineStore {
  let dbp: Promise<IDBDatabase> | null = null;
  const open = () => (dbp ??= new Promise<IDBDatabase>((resolve, reject) => {
    const r = idb.open(DB_NAME, 1);
    r.onupgradeneeded = () => {
      const db = r.result;
      if (!db.objectStoreNames.contains('responses')) db.createObjectStore('responses', { keyPath: 'key' });
      if (!db.objectStoreNames.contains('outbox')) db.createObjectStore('outbox', { keyPath: 'id', autoIncrement: true });
    };
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  }));
  const tx = async <T>(name: 'responses' | 'outbox', mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>) => {
    const db = await open();
    return req(fn(db.transaction(name, mode).objectStore(name)));
  };
  return {
    persistent: true,
    get: (k) => tx('responses', 'readonly', (s) => s.get(k) as IDBRequest<CachedResponse | undefined>),
    put: async (e) => { await tx('responses', 'readwrite', (s) => s.put(e)); },
    all: () => tx('responses', 'readonly', (s) => s.getAll() as IDBRequest<CachedResponse[]>),
    remove: async (k) => { await tx('responses', 'readwrite', (s) => s.delete(k)); },
    clear: async () => { await tx('responses', 'readwrite', (s) => s.clear()); },
    outboxAdd: async (item) => Number(await tx('outbox', 'readwrite', (s) => s.add(item))),
    outboxAll: () => tx('outbox', 'readonly', (s) => s.getAll() as IDBRequest<OutboxItem[]>),
    outboxRemove: async (id) => { await tx('outbox', 'readwrite', (s) => s.delete(id)); },
  };
}

let shared: OfflineStore | null = null;
export function getOfflineStore(): OfflineStore {
  if (shared) return shared;
  let store: OfflineStore = createMemoryStore();
  try { if (typeof indexedDB !== 'undefined' && indexedDB) store = createIdbStore(indexedDB); } catch { /* blocked */ }
  shared = store;
  return store;
}
/** Tests. */
export function setOfflineStore(s: OfflineStore | null): void { shared = s; }

let installed = false;
/** Wraps the API client's fetcher once and flushes the outbox when the browser comes back online. */
export function installOfflineCache(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  const store = getOfflineStore();
  const wrapped = createOfflineFetcher(getFetcher(), store);
  setFetcher(wrapped);
  window.addEventListener('online', () => { void flushOutbox(store).catch(() => undefined); });
}
